import { useRouter } from "next/router";
import { Roboto_Mono } from "next/font/google";

import styles from "./ContinueAssessmentsBar.module.css";

import elementsJsonData from "../../../assets/traitInfo/elements.json";
import patternsJsonData from "../../../assets/traitInfo/patterns.json";

import TimeIcon from "@/components/assessments-new/root/icons/TimeIcon";
import CheckInAssessmentSticker from "./CheckInAssessmentSticker";
import ThemedButton from "@/components/shared/ThemedButton/ThemedButton";
import {
  DimensionButtonEventStatus,
  DimensionDataType,
  dimensionIcons,
} from "@/assets/traitInfo/dimensionData";
import Element from "@/components/traits/element/Element/Element";
import Pattern from "@/components/traits/pattern/Pattern/Pattern";
import { useModalContext } from "@/context/ModalContext";
import DiscoveryGate from "@/components/shared/DiscoveryGate/DiscoveryGate";
import TraitsAssessmentModal from "@/components/TraitAssessment/TraitsAssessmentModal/TraitsAssessmentModal";

const font = Roboto_Mono({ subsets: ["latin"] });

type Props = {
  dimension: DimensionDataType;
  systemInitiated: boolean;
  estimatedCompletionTime: number;
  progress: number;
};

export default function DimensionCard(props: Props) {
  const router = useRouter();

  function handleSystemInitiatedAssessment(dim: DimensionDataType) {
    if (!dim.longFormSlug) {
      return router.push(`/app/assessment/${dim.slug}?autoStart=true`);
    } else {
      return router.push(`/app/assessment/${dim.longFormSlug}?autoStart=true`);
    }
  }

  return (
    <div
      style={{
        backgroundImage: `url(/dimensions/backgrounds/${props.dimension.slug}.jpg)`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      className={styles.assessmentDiv}
      onClick={() => {
        localStorage.setItem(
          "assessmentLoadedSource",
          "dashboard continue dimensions section"
        );
        if (props.systemInitiated) {
          return handleSystemInitiatedAssessment(props.dimension);
        }

        return router.push(`/app/assessment/${props.dimension.slug}`);
      }}
      id={props.dimension.slug}
    >
      {props.systemInitiated && (
        <div className={styles.stickerParent}>
          {<CheckInAssessmentSticker />}
        </div>
      )}
      <div className={styles.iconDiv}>
        {dimensionIcons[props.dimension.slug].icon}
      </div>
      <h4 className={styles.dimTitle}>{props.dimension.title}</h4>
      <p className={styles.dimDesc}>{props.dimension.description}</p>
      <div className={styles.timeDiv}>
        <div className={styles.timeIconDiv}>
          <TimeIcon />
        </div>
        <p className={`${styles.dimDetailsText} ${font.className}`}>
          {props.estimatedCompletionTime} minutes
        </p>
      </div>

      <ThemedButton
        style={{ marginTop: "auto" }}
        title={
          props.systemInitiated
            ? "Start"
            : props.dimension.event === "Continue"
            ? `Continue - ${(props.progress * 100).toFixed(0)}% complete`
            : "Start"
        }
      />
      {props.systemInitiated && (
        <p className={styles.textWarningBottom}>
          *Scores are only updated upon completion
        </p>
      )}
    </div>
  );
}

export function ElementDimensionCard(props: {
  slug: string;
  status: DimensionButtonEventStatus;
  gated: boolean;
}) {
  const { setModalComponent, setModalOpen } = useModalContext();

  const element = elementsJsonData.find((e) => e.slug === props.slug);

  const handleClick = () => {
    if (props.gated) {
      return setModalComponent(
        <DiscoveryGate
          requiredDimensionSlugs={[
            "personality",
            "personality-II",
            "values",
            "strengths",
            "secondary-traits",
          ]}
          onClose={() => setModalOpen(false)}
          onOpenShareLink={() => {}}
          optionalDescription="You must complete the following Dimensions in order to track your results on this trait."
        />
      );
    }

    return setModalComponent(
      <TraitsAssessmentModal
        type="element"
        slug={props.slug}
        hidden
        elementName="TraitsAssessmentModal"
        isPremium={element?.isPremium}
      />
    );
  };

  return (
    <div
      onClick={handleClick}
      id={props.slug}
      style={{ border: 0 }}
      className={styles.assessmentDiv}
    >
      <div className={styles.stickerParent}>
        {<CheckInAssessmentSticker bgColor="#2D2927" />}
      </div>
      <Element hideIcon hideName slug={props.slug} />
      <h3 className={styles.pesonalityCheckInText}>Personality check-in</h3>
      <p className={styles.checkinDesc}>
        Track how your personality has evolved over time by completing this
        check-in. Tracked trait(s) are revealed upon completion.
      </p>

      <ThemedButton
        style={{ marginTop: "auto" }}
        title={props.status === "Start" ? "Start quiz" : props.status}
      />
    </div>
  );
}

export function PatternDimensionCard(props: {
  slug: string;
  status: DimensionButtonEventStatus;
  gated: boolean;
}) {
  const { setModalComponent, setModalOpen } = useModalContext();

  const pattern = patternsJsonData.find((p) => p.slug === props.slug);
  const isBigFive = pattern?.patternGroupSlug === "pattern-group-big-five";

  const handleClick = () => {
    if (props.gated) {
      return setModalComponent(
        <DiscoveryGate
          requiredDimensionSlugs={[
            "personality",
            "personality-II",
            "values",
            "strengths",
            "secondary-traits",
          ]}
          onClose={() => setModalOpen(false)}
          onOpenShareLink={() => {}}
          optionalDescription="You must complete the following Dimensions in order to track your results on this trait."
        />
      );
    }

    return setModalComponent(
      <TraitsAssessmentModal
        type="pattern"
        slug={props.slug}
        hidden={!isBigFive}
        elementName="TraitsAssessmentModal"
        isPremium={pattern?.isPremium}
      />
    );
  };

  return (
    <div
      onClick={handleClick}
      id={props.slug}
      style={{ border: 0 }}
      className={styles.assessmentDiv}
    >
      <div className={styles.stickerParent}>
        {<CheckInAssessmentSticker bgColor="#2D2927" />}
      </div>
      <div className={styles.patternDiv}>
        <Pattern maxWidth hideName={!isBigFive} patternSlug={props.slug} />
      </div>

      <h3 className={styles.pesonalityCheckInText}>Personality check-in</h3>
      <p className={styles.checkinDesc}>
        {isBigFive
          ? `Track how you've evolved on ${pattern.name} by completing this check-in.`
          : "Track how your personality has evolved over time by completing this check-in. Tracked trait(s) are revealed upon completion."}
      </p>

      <ThemedButton
        style={{ marginTop: "auto" }}
        title={props.status === "Start" ? "Start quiz" : props.status}
      />
    </div>
  );
}
